import React, { useState } from 'react';
import {
  Box,
  Container,
  Typography,
  Grid,
  Card,
  CardContent,
  Button,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Chip,
  Alert,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,


} from '@mui/material';
import {
  ExpandMore as ExpandMoreIcon,
  Security as SecurityIcon,
  Assignment as DocumentIcon,
  Schedule as ScheduleIcon,
  CheckCircle as CheckIcon,
  Warning as WarningIcon,
  Phone as PhoneIcon,
  GetApp as DownloadIcon,
  Gavel as LegalIcon,
  Search as SearchIcon,
  Report as ReportIcon,
  MonetizationOn as MoneyIcon,
  Home as HomeIcon,
  Business as BusinessIcon,
} from '@mui/icons-material';
import { Link } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';

const coverageTypes = [
  {
    type: 'Owner\'s Policy Coverage',
    description: 'Protects the property owner\'s investment',
    scenarios: [
      'Forged deeds or documents',
      'Unknown heirs claiming ownership',
      'Errors in public records',
      'Undisclosed liens or encumbrances',
      'Survey issues and boundary disputes',
      'Easement disputes',
      'Zoning violations existing before purchase',
      'Building permit issues'
    ],
    icon: HomeIcon,
    color: '#16a34a'
  },
  {
    type: 'Lender\'s Policy Coverage',
    description: 'Protects the mortgage lender\'s interest',
    scenarios: [
      'Priority of mortgage lien',
      'Validity of mortgage documents',
      'Borrower\'s right to mortgage property',
      'Access to property for foreclosure',
      'Compliance with loan terms',
      'Protection against fraud'
    ],
    icon: BusinessIcon,
    color: '#1e40af'
  }
];

const claimsProcess = [
  {
    step: 1,
    title: 'Discover the Issue',
    description: 'Problem identified that may be covered by title insurance',
    timeline: 'Day 1',
    details: [
      'Title defect discovered during refinance or sale',
      'Third party claims ownership or rights',
      'Lien or encumbrance appears on record',
      'Boundary dispute arises with neighbor',
      'Access to property is challenged'
    ],
    action: 'Document the issue and gather initial evidence'
  },
  {
    step: 2,
    title: 'Contact Your Title Agent',
    description: 'Notify MAD Title Agency immediately',
    timeline: 'Within 24-48 hours',
    details: [
      'Call our claims hotline for immediate assistance',
      'Email detailed description with documentation',
      'Provide policy number and property details',
      'Describe the nature of the claim clearly',
      'Submit any relevant documents or notices received'
    ],
    action: 'Initial consultation and claim evaluation begins'
  },
  {
    step: 3,
    title: 'File Formal Claim',
    description: 'Submit complete claim documentation',
    timeline: 'Within 7-10 days',
    details: [
      'Complete formal claim application',
      'Provide copy of title insurance policy',
      'Submit all relevant supporting documents',
      'Include copies of any legal notices received',
      'Provide detailed statement of facts'
    ],
    action: 'Claim officially opened and assigned to specialist'
  },
  {
    step: 4,
    title: 'Investigation & Review',
    description: 'Title company investigates the claim',
    timeline: '30-60 days',
    details: [
      'Review of original title examination',
      'Investigation of public records',
      'Legal research and analysis',
      'Consultation with legal counsel if needed',
      'Communication with all parties involved'
    ],
    action: 'Determination of coverage and liability made'
  },
  {
    step: 5,
    title: 'Resolution & Payment',
    description: 'Claim resolved according to policy terms',
    timeline: '60-120 days',
    details: [
      'Covered claims: Defense or settlement provided',
      'Non-covered claims: Explanation provided',
      'Legal defense arranged if litigation required',
      'Settlement negotiations conducted',
      'Final resolution and case closure'
    ],
    action: 'Claim closed with appropriate resolution'
  }
];

const commonClaims = [
  {
    category: 'Easement Issues',
    frequency: '35%',
    description: 'Disputes over right-of-way, utility easements, or property access',
    examples: [
      'Neighbor claims driveway easement not disclosed',
      'Utility company asserts broader rights than recorded',
      'Previous owner granted easement not shown on survey'
    ],
    resolution: 'Negotiate easement terms or provide legal defense',
    timeframe: '60-90 days'
  },
  {
    category: 'Lien Priority',
    frequency: '25%',
    description: 'Disputes over which liens have priority or validity',
    examples: [
      'Mechanic\'s lien filed after closing',
      'Tax lien priority dispute',
      'Judgment lien not discovered in search'
    ],
    resolution: 'Legal challenge or settlement payment',
    timeframe: '90-180 days'
  },
  {
    category: 'Boundary Disputes',
    frequency: '20%',
    description: 'Conflicts over property lines and ownership boundaries',
    examples: [
      'Survey discrepancies between neighbors',
      'Encroachment by structures or improvements',
      'Adverse possession claims'
    ],
    resolution: 'Survey correction or legal resolution',
    timeframe: '120-240 days'
  },
  {
    category: 'Document Fraud',
    frequency: '10%',
    description: 'Forged signatures or fraudulent documents in chain of title',
    examples: [
      'Forged deed in previous transaction',
      'Identity theft in property transfer',
      'Fraudulent power of attorney'
    ],
    resolution: 'Legal action to clear title',
    timeframe: '180-365 days'
  },
  {
    category: 'Heir Claims',
    frequency: '10%',
    description: 'Unknown or omitted heirs claiming property rights',
    examples: [
      'Undisclosed heir surfaces after estate sale',
      'Improper probate proceedings',
      'Missing spouse signature on deed'
    ],
    resolution: 'Quiet title action or settlement',
    timeframe: '120-300 days'
  }
];

const requiredDocuments = [
  {
    category: 'Basic Claim Information',
    documents: [
      'Title insurance policy (original or certified copy)',
      'Property deed and chain of title documents',
      'Survey or plot plan',
      'Purchase agreement and closing documents'
    ]
  },
  {
    category: 'Issue Documentation',
    documents: [
      'Legal notices or court documents received',
      'Correspondence related to the title issue',
      'Expert opinions or professional reports',
      'Photographs of property or disputed areas'
    ]
  },
  {
    category: 'Financial Impact',
    documents: [
      'Appraisal or property valuation',
      'Receipts for related expenses incurred',
      'Loss of use or rental income documentation',
      'Legal fees and court costs'
    ]
  }
];

const TitleInsuranceClaimsProcess: React.FC = () => {
  const [expandedSection, setExpandedSection] = useState<string | false>(false);

  const handleSectionChange = (panel: string) => (event: React.SyntheticEvent, isExpanded: boolean) => {
    setExpandedSection(isExpanded ? panel : false);
  };

  return (
    <>
      <Helmet>
        <title>Title Insurance Claims Process | Complete Guide | MAD Title Agency</title>
        <meta
          name="description"
          content="Comprehensive guide to filing title insurance claims in New Jersey. Learn the process, documentation required, and what to expect when title issues arise."
        />
        <meta
          name="keywords"
          content="title insurance claims, title insurance process, title defects, New Jersey title claims, title insurance coverage, Middlesex County title issues"
        />
      </Helmet>

      {/* Breadcrumb */}
      <Box sx={{ backgroundColor: '#f8fafc', py: 2 }}>
        <Container maxWidth="lg">
          <Typography variant="body2" color="text.secondary">
            <Link to="/" style={{ textDecoration: 'none', color: 'inherit' }}>Home</Link> →{' '}
            <Link to="/resources" style={{ textDecoration: 'none', color: 'inherit' }}>Resources</Link> →{' '}
            Title Insurance Claims Process
          </Typography>
        </Container>
      </Box>

      {/* Header */}
      <Box sx={{ backgroundColor: '#dc2626', color: 'white', py: 6 }}>
        <Container maxWidth="lg">
          <Grid container spacing={4} alignItems="center">
            <Grid item xs={12} md={8}>
              <Chip
                label="Claims Assistance"
                sx={{
                  backgroundColor: '#f59e0b',
                  color: 'white',
                  mb: 2,
                }}
              />
              <Typography
                variant="h1"
                component="h1"
                sx={{
                  fontWeight: 700,
                  mb: 2,
                  fontSize: { xs: '2rem', md: '3rem' },
                }}
              >
                Title Insurance Claims Process
              </Typography>
              <Typography variant="h6" sx={{ opacity: 0.9, mb: 3 }}>
                Complete guide to understanding and filing title insurance claims in New Jersey
              </Typography>
              <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
                <Button
                  variant="contained"
                  startIcon={<PhoneIcon />}
                  sx={{
                    backgroundColor: '#f59e0b',
                    '&:hover': { backgroundColor: '#d97706' },
                  }}
                  href="tel:+17328745425"
                >
                  Claims Hotline
                </Button>
                <Button
                  variant="outlined"
                  startIcon={<DownloadIcon />}
                  sx={{
                    borderColor: 'white',
                    color: 'white',
                    '&:hover': { backgroundColor: 'rgba(255,255,255,0.1)' },
                  }}
                >
                  Download Claims Form
                </Button>
              </Box>
            </Grid>
            <Grid item xs={12} md={4}>
              <Box
                sx={{
                  backgroundColor: 'rgba(255, 255, 255, 0.1)',
                  borderRadius: 2,
                  p: 3,
                  textAlign: 'center',
                }}
              >
                <SecurityIcon sx={{ fontSize: 64, mb: 2, opacity: 0.8 }} />
                <Typography variant="h6" sx={{ fontWeight: 600 }}>
                  Claims Support
                </Typography>
                <Typography variant="body2" sx={{ opacity: 0.8 }}>
                  Expert assistance when you need it
                </Typography>
              </Box>
            </Grid>
          </Grid>
        </Container>
      </Box>

      <Container maxWidth="lg" sx={{ py: 8 }}>
        {/* Emergency Claims Contact */}
        <Alert 
          severity="error" 
          sx={{ 
            mb: 6, 
            '& .MuiAlert-icon': { fontSize: '2rem' },
            fontSize: '1.1rem'
          }}
        >
          <Typography variant="h6" sx={{ fontWeight: 600, mb: 1 }}>
            🚨 Have a Title Issue Right Now?
          </Typography>
          <Typography variant="body1">
            Don't wait! Contact our claims team immediately at{' '}
            <strong>(732) 874-5425</strong> or email{' '}
            <strong>madtitleagency@gmail.com</strong>. Early intervention often leads to faster, 
            less expensive resolutions.
          </Typography>
        </Alert>

        {/* What is Title Insurance */}
        <Box sx={{ mb: 8 }}>
          <Typography variant="h3" component="h2" sx={{ mb: 4, fontWeight: 600, color: '#dc2626' }}>
            Understanding Title Insurance Claims
          </Typography>
          <Grid container spacing={6} alignItems="center">
            <Grid item xs={12} md={8}>
              <Typography variant="body1" sx={{ mb: 3, fontSize: '1.1rem', lineHeight: 1.7 }}>
                A title insurance claim occurs when a covered title defect threatens your ownership 
                rights or creates financial loss. Unlike other types of insurance that protect against 
                future events, title insurance protects against issues that existed before you 
                purchased the property but weren't discovered during the title search.
              </Typography>
              <Typography variant="body1" sx={{ mb: 3, fontSize: '1.1rem', lineHeight: 1.7 }}>
                When a valid claim is filed, your title insurance company will either defend your 
                rights in court or compensate you for covered losses up to the policy amount. 
                This protection can save property owners tens of thousands of dollars in legal fees 
                and potential losses.
              </Typography>
              <Alert severity="info" sx={{ mb: 3 }}>
                <Typography variant="body2">
                  <strong>Important:</strong> Title insurance policies remain in effect as long as 
                  you or your heirs own the property. There's no expiration date on coverage.
                </Typography>
              </Alert>
            </Grid>
            <Grid item xs={12} md={4}>
              <Paper sx={{ p: 3, backgroundColor: '#fee2e2' }}>
                <SecurityIcon sx={{ fontSize: 48, color: '#dc2626', mb: 2 }} />
                <Typography variant="h6" sx={{ mb: 2, fontWeight: 600 }}>
                  Claims Protection
                </Typography>
                <List dense>
                  <ListItem>
                    <ListItemIcon>
                      <CheckIcon sx={{ color: '#16a34a', fontSize: 20 }} />
                    </ListItemIcon>
                    <ListItemText primary="Legal defense provided" />
                  </ListItem>
                  <ListItem>
                    <ListItemIcon>
                      <CheckIcon sx={{ color: '#16a34a', fontSize: 20 }} />
                    </ListItemIcon>
                    <ListItemText primary="Financial loss coverage" />
                  </ListItem>
                  <ListItem>
                    <ListItemIcon>
                      <CheckIcon sx={{ color: '#16a34a', fontSize: 20 }} />
                    </ListItemIcon>
                    <ListItemText primary="Expert claim handling" />
                  </ListItem>
                  <ListItem>
                    <ListItemIcon>
                      <CheckIcon sx={{ color: '#16a34a', fontSize: 20 }} />
                    </ListItemIcon>
                    <ListItemText primary="No out-of-pocket defense costs" />
                  </ListItem>
                </List>
              </Paper>
            </Grid>
          </Grid>
        </Box>

        {/* Coverage Types */}
        <Box sx={{ mb: 8 }}>
          <Typography variant="h3" component="h2" sx={{ mb: 4, fontWeight: 600, color: '#dc2626' }}>
            What Does Title Insurance Cover?
          </Typography>
          <Grid container spacing={4}>
            {coverageTypes.map((coverage, index) => (
              <Grid item xs={12} md={6} key={index}>
                <Card sx={{ height: '100%', border: `2px solid ${coverage.color}20` }}>
                  <CardContent sx={{ p: 3 }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', mb: 3 }}>
                      <Box
                        sx={{
                          backgroundColor: `${coverage.color}15`,
                          borderRadius: '50%',
                          p: 1.5,
                          mr: 2,
                        }}
                      >
                        <coverage.icon sx={{ color: coverage.color, fontSize: 32 }} />
                      </Box>
                      <Box>
                        <Typography variant="h6" sx={{ fontWeight: 600, mb: 0.5 }}>
                          {coverage.type}
                        </Typography>
                        <Typography variant="body2" color="text.secondary">
                          {coverage.description}
                        </Typography>
                      </Box>
                    </Box>
                    <Typography variant="subtitle2" sx={{ mb: 2, fontWeight: 600, color: coverage.color }}>
                      Common Covered Issues:
                    </Typography>
                    <List dense>
                      {coverage.scenarios.map((scenario, idx) => (
                        <ListItem key={idx} sx={{ py: 0.5 }}>
                          <ListItemIcon sx={{ minWidth: 30 }}>
                            <CheckIcon sx={{ color: coverage.color, fontSize: 16 }} />
                          </ListItemIcon>
                          <ListItemText 
                            primary={scenario} 
                            primaryTypographyProps={{ variant: 'body2' }}
                          />
                        </ListItem>
                      ))}
                    </List>
                  </CardContent>
                </Card>
              </Grid>
            ))}
          </Grid>
        </Box>

        {/* Claims Process Timeline */}
        <Box sx={{ mb: 8 }}>
          <Typography variant="h3" component="h2" sx={{ mb: 4, fontWeight: 600, color: '#dc2626' }}>
            Step-by-Step Claims Process
          </Typography>
          <Typography variant="body1" sx={{ mb: 4, fontSize: '1.1rem' }}>
            Here's what happens when you need to file a title insurance claim:
          </Typography>

          <Grid container spacing={4}>
            {claimsProcess.map((step, index) => (
              <Grid item xs={12} md={6} key={index}>
                <Card sx={{ height: '100%', border: '2px solid #fee2e2' }}>
                  <CardContent sx={{ p: 3 }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                      <Chip
                        label={`Step ${step.step}`}
                        sx={{ backgroundColor: '#dc2626', color: 'white', mr: 2 }}
                      />
                      <Typography variant="h6" sx={{ fontWeight: 600 }}>
                        {step.title}
                      </Typography>
                    </Box>
                    <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                      {step.description}
                    </Typography>
                    <Chip
                      label={step.timeline}
                      size="small"
                      sx={{ mb: 2, backgroundColor: '#f59e0b', color: 'white' }}
                    />
                    
                    <Typography variant="h6" sx={{ mb: 2, fontWeight: 600 }}>
                      What Happens:
                    </Typography>
                    <List dense>
                      {step.details.map((detail, detailIndex) => (
                        <ListItem key={detailIndex} sx={{ py: 0.25 }}>
                          <ListItemIcon sx={{ minWidth: 25 }}>
                            <CheckIcon sx={{ color: '#16a34a', fontSize: 16 }} />
                          </ListItemIcon>
                          <ListItemText 
                            primary={detail}
                            primaryTypographyProps={{ variant: 'body2' }}
                          />
                        </ListItem>
                      ))}
                    </List>

                    <Alert severity="info" sx={{ mt: 2 }}>
                      <Typography variant="body2">
                        <strong>Next Step:</strong> {step.action}
                      </Typography>
                    </Alert>
                  </CardContent>
                </Card>
              </Grid>
            ))}
          </Grid>
        </Box>

        {/* Common Types of Claims */}
        <Box sx={{ mb: 8 }}>
          <Typography variant="h3" component="h2" sx={{ mb: 4, fontWeight: 600, color: '#dc2626' }}>
            Most Common Title Insurance Claims
          </Typography>
          <TableContainer component={Paper}>
            <Table>
              <TableHead>
                <TableRow sx={{ backgroundColor: '#f8fafc' }}>
                  <TableCell><strong>Claim Type</strong></TableCell>
                  <TableCell><strong>Frequency</strong></TableCell>
                  <TableCell><strong>Description</strong></TableCell>
                  <TableCell><strong>Typical Resolution</strong></TableCell>
                  <TableCell><strong>Timeframe</strong></TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {commonClaims.map((claim, index) => (
                  <TableRow key={index}>
                    <TableCell>
                      <Typography variant="body2" sx={{ fontWeight: 600 }}>
                        {claim.category}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Chip 
                        label={claim.frequency} 
                        size="small" 
                        sx={{ backgroundColor: '#dc2626', color: 'white' }}
                      />
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2">
                        {claim.description}
                      </Typography>
                      <List dense sx={{ mt: 1 }}>
                        {claim.examples.map((example, idx) => (
                          <ListItem key={idx} sx={{ py: 0, pl: 0 }}>
                            <Typography variant="caption" color="text.secondary">
                              • {example}
                            </Typography>
                          </ListItem>
                        ))}
                      </List>
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2">
                        {claim.resolution}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2" sx={{ fontWeight: 600, color: '#dc2626' }}>
                        {claim.timeframe}
                      </Typography>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        </Box>

        {/* Required Documentation */}
        <Box sx={{ mb: 8 }}>
          <Typography variant="h3" component="h2" sx={{ mb: 4, fontWeight: 600, color: '#dc2626' }}>
            Documentation Required for Claims
          </Typography>
          <Typography variant="body1" sx={{ mb: 4, fontSize: '1.1rem' }}>
            Gather these documents when filing your title insurance claim:
          </Typography>
          <Grid container spacing={4}>
            {requiredDocuments.map((category, index) => (
              <Grid item xs={12} md={4} key={index}>
                <Card sx={{ height: '100%' }}>
                  <CardContent sx={{ p: 3 }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', mb: 3 }}>
                      <DocumentIcon sx={{ color: '#dc2626', mr: 2, fontSize: 32 }} />
                      <Typography variant="h6" sx={{ fontWeight: 600 }}>
                        {category.category}
                      </Typography>
                    </Box>
                    <List>
                      {category.documents.map((document, docIndex) => (
                        <ListItem key={docIndex}>
                          <ListItemIcon>
                            <CheckIcon sx={{ color: '#16a34a', fontSize: 20 }} />
                          </ListItemIcon>
                          <ListItemText 
                            primary={document}
                            primaryTypographyProps={{ variant: 'body2' }}
                          />
                        </ListItem>
                      ))}
                    </List>
                  </CardContent>
                </Card>
              </Grid>
            ))}
          </Grid>
        </Box>

        {/* Claims FAQ */}
        <Box sx={{ mb: 8 }}>
          <Typography variant="h3" component="h2" sx={{ mb: 4, fontWeight: 600, color: '#dc2626' }}>
            Claims Process FAQ
          </Typography>

          <Accordion
            expanded={expandedSection === 'faq1'}
            onChange={handleSectionChange('faq1')}
            sx={{ mb: 2 }}
          >
            <AccordionSummary expandIcon={<ExpandMoreIcon />}>
              <Typography variant="h6" sx={{ fontWeight: 600 }}>
                How long does the claims process take?
              </Typography>
            </AccordionSummary>
            <AccordionDetails>
              <Typography variant="body1" sx={{ mb: 2 }}>
                The timeline varies based on claim complexity. Simple administrative corrections 
                may resolve in 30-60 days, while complex legal disputes can take 6-18 months. 
                We keep you informed throughout the process and work to resolve claims as quickly as possible.
              </Typography>
              <Alert severity="info">
                <Typography variant="body2">
                  Emergency situations (like pending foreclosures) receive expedited handling.
                </Typography>
              </Alert>
            </AccordionDetails>
          </Accordion>

          <Accordion
            expanded={expandedSection === 'faq2'}
            onChange={handleSectionChange('faq2')}
            sx={{ mb: 2 }}
          >
            <AccordionSummary expandIcon={<ExpandMoreIcon />}>
              <Typography variant="h6" sx={{ fontWeight: 600 }}>
                Will I have to pay legal fees during a claim?
              </Typography>
            </AccordionSummary>
            <AccordionDetails>
              <Typography variant="body1" sx={{ mb: 2 }}>
                No! For covered claims, the title insurance company pays all legal defense costs. 
                This includes attorney fees, court costs, and expert witness fees. You won't 
                receive any bills for legal defense of your title.
              </Typography>
              <Alert severity="success">
                <Typography variant="body2">
                  This benefit alone often exceeds the one-time premium paid for title insurance.
                </Typography>
              </Alert>
            </AccordionDetails>
          </Accordion>

          <Accordion
            expanded={expandedSection === 'faq3'}
            onChange={handleSectionChange('faq3')}
            sx={{ mb: 2 }}
          >
            <AccordionSummary expandIcon={<ExpandMoreIcon />}>
              <Typography variant="h6" sx={{ fontWeight: 600 }}>
                What if my claim is denied?
              </Typography>
            </AccordionSummary>
            <AccordionDetails>
              <Typography variant="body1" sx={{ mb: 2 }}>
                If a claim is denied, you'll receive a detailed explanation of why the issue 
                isn't covered under your policy. You have the right to appeal the decision 
                or seek independent legal advice. Common reasons for denial include:
              </Typography>
              <List>
                <ListItem>
                  <ListItemIcon>
                    <WarningIcon sx={{ color: '#f59e0b' }} />
                  </ListItemIcon>
                  <ListItemText primary="Issue was specifically excluded in the policy" />
                </ListItem>
                <ListItem>
                  <ListItemIcon>
                    <WarningIcon sx={{ color: '#f59e0b' }} />
                  </ListItemIcon>
                  <ListItemText primary="Problem arose after the policy date" />
                </ListItem>
                <ListItem>
                  <ListItemIcon>
                    <WarningIcon sx={{ color: '#f59e0b' }} />
                  </ListItemIcon>
                  <ListItemText primary="Issue was known to you at purchase" />
                </ListItem>
              </List>
            </AccordionDetails>
          </Accordion>
        </Box>

        {/* MAD Title Claims Advantage */}
        <Box sx={{ mb: 8 }}>
          <Typography variant="h3" component="h2" sx={{ mb: 4, fontWeight: 600, color: '#dc2626' }}>
            MAD Title Claims Advantage
          </Typography>
          <Grid container spacing={4}>
            <Grid item xs={12} md={6}>
              <Card sx={{ p: 3, height: '100%', backgroundColor: '#f0fdf4' }}>
                <Typography variant="h6" sx={{ mb: 3, fontWeight: 600 }}>
                  ✅ Why Choose MAD Title for Claims
                </Typography>
                <List>
                  <ListItem>
                    <ListItemIcon>
                      <CheckIcon sx={{ color: '#16a34a' }} />
                    </ListItemIcon>
                    <ListItemText
                      primary="Local Expertise"
                      secondary="Deep knowledge of New Jersey title law and local court procedures"
                    />
                  </ListItem>
                  <ListItem>
                    <ListItemIcon>
                      <CheckIcon sx={{ color: '#16a34a' }} />
                    </ListItemIcon>
                    <ListItemText
                      primary="Experienced Claims Team"
                      secondary="Dedicated professionals who handle claims daily"
                    />
                  </ListItem>
                  <ListItem>
                    <ListItemIcon>
                      <CheckIcon sx={{ color: '#16a34a' }} />
                    </ListItemIcon>
                    <ListItemText
                      primary="Personal Service"
                      secondary="Direct access to your claims specialist and regular updates"
                    />
                  </ListItem>
                  <ListItem>
                    <ListItemIcon>
                      <CheckIcon sx={{ color: '#16a34a' }} />
                    </ListItemIcon>
                    <ListItemText
                      primary="Fast Response"
                      secondary="24-48 hour initial response to all claims notifications"
                    />
                  </ListItem>
                </List>
              </Card>
            </Grid>
            <Grid item xs={12} md={6}>
              <Card sx={{ p: 3, height: '100%', backgroundColor: '#fee2e2' }}>
                <Typography variant="h6" sx={{ mb: 3, fontWeight: 600 }}>
                  📞 How to Reach Our Claims Team
                </Typography>
                <List>
                  <ListItem>
                    <ListItemIcon>
                      <PhoneIcon sx={{ color: '#dc2626' }} />
                    </ListItemIcon>
                    <ListItemText
                      primary="Claims Hotline"
                      secondary="(732) 874-5425 - Available 24/7 for urgent matters"
                    />
                  </ListItem>
                  <ListItem>
                    <ListItemIcon>
                      <ReportIcon sx={{ color: '#dc2626' }} />
                    </ListItemIcon>
                    <ListItemText
                      primary="Email Claims"
                      secondary="madtitleagency@gmail.com - Include policy number and property address"
                    />
                  </ListItem>
                  <ListItem>
                    <ListItemIcon>
                      <DocumentIcon sx={{ color: '#dc2626' }} />
                    </ListItemIcon>
                    <ListItemText
                      primary="Online Portal"
                      secondary="Submit claims and track status through our secure portal"
                    />
                  </ListItem>
                  <ListItem>
                    <ListItemIcon>
                      <LegalIcon sx={{ color: '#dc2626' }} />
                    </ListItemIcon>
                    <ListItemText
                      primary="In-Person Consultation"
                      secondary="Schedule appointment for complex claims review"
                    />
                  </ListItem>
                </List>
              </Card>
            </Grid>
          </Grid>
        </Box>

        {/* Related Resources */}
        <Box sx={{ mb: 8 }}>
          <Typography variant="h3" component="h2" sx={{ mb: 4, fontWeight: 600, color: '#dc2626' }}>
            Related Resources
          </Typography>
          <Grid container spacing={4}>
            <Grid item xs={12} sm={6} md={4}>
              <Card sx={{ p: 3, textAlign: 'center', height: '100%' }}>
                <SearchIcon sx={{ fontSize: 48, color: '#dc2626', mb: 2 }} />
                <Typography variant="h6" sx={{ mb: 2, fontWeight: 600 }}>
                  Title Search Manual
                </Typography>
                <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
                  Understanding how title searches prevent future claims.
                </Typography>
                <Button
                  variant="outlined"
                  component={Link}
                  to="/resources/title-search-manual"
                  fullWidth
                >
                  View Manual
                </Button>
              </Card>
            </Grid>
            <Grid item xs={12} sm={6} md={4}>
              <Card sx={{ p: 3, textAlign: 'center', height: '100%' }}>
                <WarningIcon sx={{ fontSize: 48, color: '#dc2626', mb: 2 }} />
                <Typography variant="h6" sx={{ mb: 2, fontWeight: 600 }}>
                  Title Defects Guide
                </Typography>
                <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
                  Common title problems and prevention strategies.
                </Typography>
                <Button
                  variant="outlined"
                  component={Link}
                  to="/resources/title-defects-guide"
                  fullWidth
                >
                  Learn More
                </Button>
              </Card>
            </Grid>
            <Grid item xs={12} sm={6} md={4}>
              <Card sx={{ p: 3, textAlign: 'center', height: '100%' }}>
                <MoneyIcon sx={{ fontSize: 48, color: '#dc2626', mb: 2 }} />
                <Typography variant="h6" sx={{ mb: 2, fontWeight: 600 }}>
                  Title Insurance Guide
                </Typography>
                <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
                  Complete guide to title insurance coverage and benefits.
                </Typography>
                <Button
                  variant="outlined"
                  component={Link}
                  to="/resources/title-insurance-guide"
                  fullWidth
                >
                  Read Guide
                </Button>
              </Card>
            </Grid>
          </Grid>
        </Box>
      </Container>

      {/* CTA Section */}
      <Box sx={{ backgroundColor: '#fee2e2', py: 8 }}>
        <Container maxWidth="lg">
          <Box sx={{ textAlign: 'center' }}>
            <Typography variant="h3" component="h2" sx={{ mb: 2, fontWeight: 600, color: '#64748b' }}>
              Need to File a Title Insurance Claim?
            </Typography>
            <Typography variant="h6" sx={{ mb: 4, color: 'text.secondary' }}>
              Our experienced claims team is here to help. Don't let title issues threaten 
              your property investment - contact us immediately for expert assistance.
            </Typography>
            <Box sx={{ display: 'flex', gap: 2, justifyContent: 'center', flexWrap: 'wrap' }}>
              <Button
                variant="contained"
                size="large"
                href="tel:+17328745425"
                startIcon={<PhoneIcon />}
                sx={{
                  background: 'linear-gradient(135deg, #dc2626 0%, #ef4444 100%)',
                  px: 4,
                  py: 1.5,
                  fontSize: '1.1rem',
                }}
              >
                Call Claims Hotline
              </Button>
              <Button
                variant="outlined"
                size="large"
                href="mailto:madtitleagency@gmail.com"
                sx={{
                  borderColor: '#dc2626',
                  color: '#dc2626',
                  px: 4,
                  py: 1.5,
                  fontSize: '1.1rem',
                }}
              >
                Email Claims Team
              </Button>
            </Box>
          </Box>
        </Container>
      </Box>
    </>
  );
};

export default TitleInsuranceClaimsProcess; 